/**
 * Provider di tema e localizzazione di antd per l'intera dashboard.
 *
 * È l'unico punto in cui si configura la libreria UI: lingua, locale di antd,
 * token di design e algoritmo di tema. Un componente che volesse un
 * `ConfigProvider` proprio introdurrebbe una seconda fonte di verità
 * sull'aspetto, quindi non ne esistono altri.
 *
 * I valori non stanno qui: stanno in `tokens.ts`, che non importa React e si
 * può leggere senza montare nulla. Questo file li applica e basta.
 *
 * Dal 2026-09-20 il provider è anche il punto in cui la lingua diventa
 * visibile: `I18nextProvider` la porta ai componenti, il `locale` di antd la
 * segue (altrimenti `Empty` direbbe «No data» dentro una pagina italiana) e
 * `document.documentElement.lang` la dichiara al browser — che è ciò che
 * decide la sillabazione, la voce dello screen reader e l'offerta di tradurre
 * la pagina.
 *
 * ## Il tema, dal 2026-09-20
 *
 * Tre stati, non due: «sistema», «chiaro», «scuro» (il perché sta in
 * `themeMode.ts`). Il provider tiene lo stato scelto, lo risolve con la
 * preferenza del sistema e ne ricava tre cose coerenti fra loro:
 *
 *  1. l'**algoritmo** di antd (`darkAlgorithm` o `defaultAlgorithm`), che
 *     deriva tutti i token dei componenti;
 *  2. i **token globali** del tema, che ritoccano ciò che l'algoritmo porta
 *     sotto soglia di contrasto (`tokens.ts`);
 *  3. gli attributi `data-theme` e `color-scheme` sull'elemento radice, senza i
 *     quali la pagina intorno ai componenti, le barre di scorrimento e i
 *     controlli nativi resterebbero del tema di prima.
 *
 * Lo stato **iniziale** si legge in modo sincrono (`initialThemeMode`), non in
 * un effetto: un effetto gira dopo il primo render, e il primo render con il
 * tema sbagliato è esattamente il lampo che si vuole evitare.
 */

import { App as AntApp, ConfigProvider, theme } from 'antd';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import { antdLocaleFor } from '../i18n/antdLocale';
import { currentLanguage, i18n } from '../i18n';
import { COMPACT_CELL_PADDING_BLOCK, readCompactTables, storeCompactTables } from './density';
import { componentTokensFor, globalTokensFor } from './tokens';
import {
  applyTheme,
  initialThemeMode,
  resolveTheme,
  storeThemeMode,
  type ThemeMode,
} from './themeMode';
import { ThemeModeContext, type ThemeModeValue } from './useThemeMode';
import { usePrefersDark } from './usePrefersDark';

/** Proprietà del provider. */
export interface InstitorConfigProviderProps {
  /** Albero dell'applicazione da configurare. */
  children: ReactNode;
}

/**
 * Applica tema e locale di antd alla lingua attiva.
 *
 * Sta in un componente interno perché deve stare **dentro**
 * `I18nextProvider`: `useTranslation` serve solo a farsi ridisegnare al cambio
 * di lingua, e il valore vero lo legge `currentLanguage()`.
 *
 * @param props.children Albero dell'applicazione.
 * @returns L'albero configurato.
 */
function ThemedApp({ children }: InstitorConfigProviderProps): JSX.Element {
  const prefersDark = usePrefersDark();
  useTranslation();
  const language = currentLanguage();

  const [mode, setModeState] = useState<ThemeMode>(initialThemeMode);
  const resolved = resolveTheme(mode, prefersDark);

  // L'attributo `lang` del documento non lo gestisce React: sta fuori dalla
  // radice dell'applicazione, in `index.html`, e va aggiornato a mano.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Idem per il tema: `data-theme` e `color-scheme` stanno sull'elemento
  // radice, che è fuori dall'albero di React. All'avvio li ha già scritti
  // `theme-boot.js`, quindi questo effetto conta per i cambi successivi — e
  // nei test, dove quello script non gira.
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    // Il salvataggio è l'ultimo passo, come per la lingua: se fallisce, il tema
    // è comunque cambiato per questa sessione.
    storeThemeMode(next);
  }, []);

  const [compactTables, setCompactState] = useState<boolean>(readCompactTables);
  const setCompactTables = useCallback((compact: boolean) => {
    setCompactState(compact);
    storeCompactTables(compact);
  }, []);

  const themeValue = useMemo<ThemeModeValue>(
    () => ({ mode, resolved, setMode, compactTables, setCompactTables }),
    [mode, resolved, setMode, compactTables, setCompactTables],
  );

  // Le tabelle compatte cambiano solo il padding verticale delle celle: un
  // token del componente, non un secondo `ConfigProvider` né una regola CSS.
  const components = useMemo(() => {
    const base = componentTokensFor(resolved);
    if (!compactTables || base === undefined) {
      return base;
    }
    return {
      ...base,
      Table: {
        ...base.Table,
        cellPaddingBlock: COMPACT_CELL_PADDING_BLOCK,
        cellPaddingBlockSM: COMPACT_CELL_PADDING_BLOCK,
      },
    };
  }, [resolved, compactTables]);

  return (
    <ThemeModeContext.Provider value={themeValue}>
      <ConfigProvider
        locale={antdLocaleFor(language)}
        theme={{
          algorithm: resolved === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
          token: globalTokensFor(resolved),
          components,
        }}
      >
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </ThemeModeContext.Provider>
  );
}

/**
 * Configura lingua, tema e locale di antd per tutta l'applicazione.
 *
 * Avvolge i figli in `I18nextProvider` (lingua), nel contesto del tema,
 * `ConfigProvider` (locale, token, algoritmo) e `App`, che fornisce il contesto
 * necessario alle API imperative di antd (`message`, `modal`, `notification`)
 * perché rispettino il tema invece di usare quello di default. Ogni pagina le
 * ottiene con `App.useApp()`, mai dagli import statici.
 *
 * @param props.children Albero dell'applicazione.
 * @returns L'albero configurato.
 */
export function InstitorConfigProvider({ children }: InstitorConfigProviderProps): JSX.Element {
  return (
    <I18nextProvider i18n={i18n}>
      <ThemedApp>{children}</ThemedApp>
    </I18nextProvider>
  );
}
